/**
 * Data mapping utilities for subscription form.
 * Maps backend data structures to form-compatible formats.
 */

import { SubscriptionV2Variable } from "@/pages/subscriptions/services/types";
import {
  formatAggregation,
  formatChangeType,
  formatComparison,
  formatDateRange,
  formatSchedule,
} from "./subscriptionFormFormatters";
import { ChannelType, VariableType } from "@/pages/subscriptions/constants";

export interface BackendParams {
  aggregation?: string;
  comparison_type?: string;
  threshold_type?: string;
  threshold_value?: number;
  is_percentage?: boolean | null;
  date_range?: string;
}

export interface ScheduleConfig {
  cron?: string;
}

export interface NotifierConfig {
  type: ChannelType;
  recipients?: string[];
  channel?: string;
}

export interface NotifierExtraction {
  channels: ChannelType[];
  emailRecipients: string[];
  slackChannels: string[];
}

interface DisplayNameLookups {
  categoryDisplayNameLookup: Record<string, string>;
  metricDisplayNameLookup: Record<string, string>;
}

/**
 * Maps backend params and schedule config to form variables with proper display values.
 */
export const mapBackendToFormVariables = (
  variables: SubscriptionV2Variable[],
  params: BackendParams,
  scheduleConfig: ScheduleConfig | undefined,
  source: string,
  metric: string,
  lookups: DisplayNameLookups
): SubscriptionV2Variable[] => {
  const { categoryDisplayNameLookup, metricDisplayNameLookup } = lookups;

  return variables.map(v => {
    const variable: SubscriptionV2Variable = { ...v, variableId: v.id };

    // Populate entity with proper display name from metrics
    if (v.type === VariableType.ENTITY) {
      const categoryDisplayName = categoryDisplayNameLookup[source];
      if (categoryDisplayName) {
        variable.displayValue = categoryDisplayName;
      }
    }

    // Populate metric with proper display name from metrics
    if (v.type === VariableType.METRIC) {
      const metricDisplayName = metricDisplayNameLookup[metric];
      if (metricDisplayName) {
        variable.displayValue = metricDisplayName;
      }
    }

    // Populate aggregation
    if (v.type === VariableType.AGGREGATION && params.aggregation) {
      variable.value = params.aggregation;
      variable.displayValue = formatAggregation(params.aggregation);
    }

    // Populate change_type
    if (v.type === VariableType.CHANGE_TYPE && params.comparison_type) {
      variable.value = params.comparison_type;
      variable.displayValue = formatChangeType(params.comparison_type);
    }

    // Populate comparison
    if (v.type === VariableType.COMPARISON && params.threshold_type) {
      variable.value = params.threshold_type;
      variable.displayValue = formatComparison(params.threshold_type);
    }

    // Populate threshold
    if (v.type === VariableType.THRESHOLD && params.threshold_value !== undefined) {
      const isPercentage = params.is_percentage === true;
      const thresholdStr = String(params.threshold_value);
      variable.value = isPercentage ? `${thresholdStr}%` : thresholdStr;
      variable.displayValue = isPercentage ? `${thresholdStr}%` : thresholdStr;
    }

    // Populate date_range
    if (v.type === VariableType.DATE_RANGE && params.date_range) {
      variable.value = params.date_range;
      variable.displayValue = formatDateRange(params.date_range);
    }

    // Populate schedule
    if (v.type === VariableType.SCHEDULE && scheduleConfig?.cron) {
      const { value, displayValue } = formatSchedule(scheduleConfig.cron);
      variable.value = value;
      variable.displayValue = displayValue;
    }

    return variable;
  });
};

/**
 * Extracts notification channels and recipients from backend notifier config.
 */
export const extractNotifierConfig = (notifierConfig: NotifierConfig[]): NotifierExtraction => {
  const channels: ChannelType[] = [];
  const emailRecipients: string[] = [];
  const slackChannels: string[] = [];

  notifierConfig.forEach(notifier => {
    if (notifier.type === ChannelType.EMAIL && notifier.recipients) {
      if (!channels.includes(ChannelType.EMAIL)) channels.push(ChannelType.EMAIL);
      emailRecipients.push(...notifier.recipients);
    }
    if (notifier.type === ChannelType.SLACK && notifier.channel) {
      if (!channels.includes(ChannelType.SLACK)) channels.push(ChannelType.SLACK);
      slackChannels.push(notifier.channel);
    }
  });

  return { channels, emailRecipients, slackChannels };
};
